import Konva from "konva";
import { Vector2d } from "konva/lib/types";
import { ReactNode, RefObject, useEffect, useState } from "react";
import { Stage } from "react-konva";
import {
  setCoords,
  setStagePosition,
} from "@features/annogram/canvas/state/canvasAuxSlice";
import { addRectangle } from "@store/slices/workspaceSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { selectCurrentCanvas } from "@store/selectors";
import {
  autoLabel,
  collapsedDisplayBody,
  collapsedRectSize,
} from "@shared/utils/utils";
import TextAreaModal from "@shared/components/TextAreaModal";

interface Props {
  stageRef: RefObject<Konva.Stage> | null;
  children: ReactNode;
}

const CanvasStage = ({ stageRef, children }: Props) => {
  const currentCanvas = useAppSelector(selectCurrentCanvas);
  const dispatch = useAppDispatch();
  const canvasAuxState = useAppSelector((state) => state.canvasAux);
  const stageCoordinates = canvasAuxState.stagePosition;

  const [isOpenTextModal, setOpenTextModal] = useState<boolean>(false);

  /* window.innerHeight and innerWidth 
  because they fit the canvas into the dimensions of the browser window.
  Width is scaled by 75% because the argument-navbar takes 25%.
 */
  const initialSceneHeight = window.innerHeight;
  const initialSceneWidth = window.innerWidth * 0.75;

  /* The point of this useEffect hook is to enable the resizing of the stage and the objects in it
   based on changes in window size 
  We might not need this functionality, because the stage is draggable.
  I'm also thinking of implementing a mouse scroll zoom which further reduces the need for that. */

  useEffect(() => {
    function fitStageIntoParentContainer() {
      if (stageRef && stageRef.current) {
        const container: HTMLElement | null = document.querySelector(
          ".argument-canvas-container"
        );
        let containerWidth = initialSceneWidth;
        let containerHeight = initialSceneHeight;

        if (container) {
          containerWidth = container.offsetWidth;
          containerHeight = container.offsetHeight;
        }
        let xScale = containerWidth / initialSceneWidth;
        let yScale = containerHeight / initialSceneHeight;

        stageRef.current.width(initialSceneWidth * xScale);
        stageRef.current.height(initialSceneHeight * yScale);
      }
    }
    fitStageIntoParentContainer();
    window.addEventListener("resize", fitStageIntoParentContainer);
    return () =>
      window.removeEventListener("resize", fitStageIntoParentContainer);
  }, [initialSceneHeight, initialSceneWidth, stageRef]);

  const handleDoubleClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    const targetType = event.target.getType();
    const stage = stageRef?.current;

    if (targetType !== "Stage" || !stage) return;

    // Relative position: accounts for the stage's pan and zoom, so the
    // new node lands where the user double-clicked rather than at the
    // equivalent screen offset from the canvas origin.
    const position = stage.getRelativePointerPosition();

    if (!position) return;

    const { x, y } = position;

    dispatch(setCoords({ x, y }));
    setOpenTextModal(true);
  };

  const handleSubmitCustomTextRect = (
    e: React.MouseEvent<HTMLButtonElement>,
    input: string
  ) => {
    e.preventDefault();

    // Direct-create gets auto-abbreviated by default: the typed input
    // becomes the full text, and a short autoLabel is shown collapsed.
    // No second modal — keeps the double-click-to-add flow fast. The
    // user can still rename via the rect context menu.
    const tag = autoLabel(input);
    dispatch(
      addRectangle({
        ...canvasAuxState.coords,
        ...collapsedRectSize(collapsedDisplayBody(tag, input), false),
        category: "user_created",
        text: input,
        expanded: false,
        tag,
        arrows: [],
      })
    );
    setOpenTextModal(false);
  };

  const handleDrag = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (event.target.getType() !== "Stage") return;

    dispatch(
      setStagePosition({ x: event.target.x(), y: event.target.y() })
    );
  };

  const handleContextMenu = (event: Konva.KonvaEventObject<MouseEvent>) => {
    event.evt.preventDefault();
    const stage = stageRef?.current;

    if (!stage) return;

    const { x, y } = stage.getPointerPosition() || {};
    const { left, top } = stage.container().getBoundingClientRect() || {};

    if (!left || !top || !x || !y) return;

    const coords = {
      x: left + x + 4,
      y: top + y + 4,
    };
    dispatch(setCoords(coords));
  };

  return (
    <>
      <Stage
        x={stageCoordinates.x}
        y={stageCoordinates.y}
        height={initialSceneHeight}
        width={initialSceneWidth}
        ref={stageRef}
        scale={{ x: currentCanvas?.canvas_data?.scale || 1, y: currentCanvas?.canvas_data?.scale || 1 }}
        //onWheel={handleWheel}
        draggable
        onDragStart={handleDrag}
        onDragMove={handleDrag}
        onDragEnd={handleDrag}
        onDblClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        {children}
      </Stage>
      {isOpenTextModal ? (
        <TextAreaModal
          title={"Create a Text Rectangle:"}
          handleCancel={() => setOpenTextModal(false)}
          handleSubmit={handleSubmitCustomTextRect}
        />
      ) : null}
    </>
  );
};

export default CanvasStage;
